import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6499mb0723ekv2itnfg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contactez-Nous
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Réservez votre service de nettoyage mobile ou demandez un devis personnalisé
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] rounded-2xl p-8 text-white mb-8">
              <h3 className="text-2xl font-bold mb-6">Informations de Contact</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-white/80 mb-1">Téléphone</p>
                    <a href="tel:0695104394" className="text-lg font-semibold hover:text-white/90 transition-colors whitespace-nowrap">
                      06 95 10 43 94
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-instagram-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-white/80 mb-1">Instagram</p>
                    <a href="https://instagram.com/BR_.clean63" target="_blank" rel="noopener noreferrer" className="text-lg font-semibold hover:text-white/90 transition-colors whitespace-nowrap">
                      @BR_.clean63
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-white/80 mb-1">Zone de Service</p>
                    <p className="text-lg font-semibold">Clermont-Ferrand et environs</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-time-line text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-white/80 mb-1">Horaires</p>
                    <p className="text-lg font-semibold">Sur rendez-vous</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Pourquoi Nous Choisir ?</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-star-fill text-[#FBBF24] text-lg mt-0.5 mr-3"></i>
                  <span className="text-sm text-gray-700">Noté 5 étoiles sur Google</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-double-line text-[#0EA5E9] text-lg mt-0.5 mr-3"></i>
                  <span className="text-sm text-gray-700">Service mobile à domicile ou au bureau</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-double-line text-[#0EA5E9] text-lg mt-0.5 mr-3"></i>
                  <span className="text-sm text-gray-700">Produits et équipements professionnels</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-double-line text-[#0EA5E9] text-lg mt-0.5 mr-3"></i>
                  <span className="text-sm text-gray-700">Équipe expérimentée et passionnée</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-double-line text-[#0EA5E9] text-lg mt-0.5 mr-3"></i>
                  <span className="text-sm text-gray-700">Tarifs transparents et compétitifs</span>
                </li>
              </ul>
            </div>
          </div>

          <div>
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8" data-readdy-form>
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                    Nom Complet *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent outline-none transition-all"
                    placeholder="Votre nom"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent outline-none transition-all"
                    placeholder="votre@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent outline-none transition-all"
                    placeholder="06 00 00 00 00"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-900 mb-2">
                    Service Souhaité *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent outline-none transition-all cursor-pointer"
                  >
                    <option value="">Sélectionnez un service</option>
                    <option value="Nettoyage Intérieur - 80€">Nettoyage Intérieur - 80€</option>
                    <option value="Nettoyage Extérieur - 30€">Nettoyage Extérieur - 30€</option>
                    <option value="Intérieur + Extérieur - 100€">Intérieur + Extérieur - 100€</option>
                    <option value="Polissage sur devis">Polissage sur devis</option>
                    <option value="Traitement céramique">Traitement céramique</option>
                    <option value="Rénovation de phares - 50€">Rénovation de phares - 50€</option>
                    <option value="Pressing sièges - 40€">Pressing sièges - 40€</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Détails supplémentaires, adresse, date souhaitée..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 caractères</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0EA5E9] text-white py-4 rounded-lg font-semibold hover:bg-[#0284C7] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer la Demande'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    Merci ! Votre demande a été envoyée avec succès. Nous vous contacterons bientôt.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    Une erreur s'est produite. Veuillez réessayer ou nous contacter directement.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}